import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../_base-shared/contracts/laravel-response.interface';
import { Creditor } from '../../../../../_base-shared/models/Entity/Creditor';
import { MainBaseApiService } from '../../_shared/services/main-base-api.service';

@Injectable({
  providedIn: 'root',
})
export class CreditorService extends MainBaseApiService {

  public index(data): Observable<LaravelResourceResponse<Array<Creditor>>> {
    return this.http.get<LaravelResourceResponse<Array<Creditor>>>(`${ this.apiUrl }/creditors`, {params: data})
      .pipe(catchError(response => this.handleError(response)));
  }

  getCreditorCaseCount(creditorId: number): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }/case-count`)
      .pipe(catchError(response => this.handleError(response)));
  }

  get(creditorId: number): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }`)
      .pipe(catchError(response => this.handleError(response)));
  }

  store(newCreditor: Creditor): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(`${ this.apiUrl }/creditors`, newCreditor)
      .pipe(catchError(response => this.handleError(response)));
  }

  update(creditorId: number, creditor: Creditor): Observable<LaravelResourceResponse> {
    return this.http.put<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }`, creditor)
      .pipe(
        catchError(response => this.handleError(response)),
        tap(response => {
          this.globalEvents.setUnapprovedCreditors(response.data.unapproved_creditors);
        }));
  }

  delete(creditorId: number): Observable<LaravelResourceResponse> {
    return this.http.delete<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }`)
      .pipe(catchError(response => this.handleError(response)));
  }

  deleteAndReassign(deleteId: number, reassignId: number, notes: string): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(`${ this.apiUrl }/creditors/reassign`,
      {reassign: reassignId, delete: deleteId, notes})
      .pipe(catchError(response => this.handleError(response)));
  }

  showReport(creditorId: number, data): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }/show-report`, {params: data})
      .pipe(catchError(response => this.handleError(response)));
  }

  showReportAll(): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/show-full-report`)
      .pipe(catchError(response => this.handleError(response)));
  }

  downloadReport(creditorId: number): Observable<LaravelResourceResponse<any>> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }/download-report`)
      .pipe(catchError(response => this.handleError(response)));
  }

  verifySendMandates(caseCreditorId, data = null): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/verify-send-mandate`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendMandatesRecoveryAgent(caseCreditorId, data = null): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/verify-send-mandate-recovery-agent`,
      data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendProposalAndAcoord(caseId: number, data: { selected_ids: Array<number> }): Observable<object> {
    return this.http.post(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-proposal-and-accord', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  updateCasePublicDebt(caseId, debtId, data): Observable<object> {
    return this.http.patch(`${ this.apiUrl }/cases/${ caseId }/public-debts/${ debtId }`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendDeleteNumberEmail(caseCreditorId, data): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/send-creditor-delete-phone`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendDeleteNumberUnsecured(caseId: number, creditorIds: Array<number>): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(
      `${ this.apiUrl }/cases/${ caseId }/case-creditors/actions/send-creditor-delete-phone`,
      {case_creditor_ids: creditorIds})
      .pipe(catchError(response => this.handleError(response)));
  }

  public sendExtrajudicialClaim(caseId: number, data: any): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-extrajudicial-claim',
      data)
      .pipe(catchError(response => this.handleError(response)));
  }


  public fetchPublicCreditors(): Observable<LaravelResourceResponse<any>> {
    return this.http.get<LaravelResourceResponse>(this.apiUrl + '/public-organizations', {})
      .pipe(catchError(response => this.handleError(response)));
  }

  public exportCreditors(filters): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/export`, {params: filters})
      .pipe(catchError(response => this.handleError(response)));
  }
}
